import { createCommentVNode as _createCommentVNode, renderSlot as _renderSlot, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createElementVNode as _createElementVNode, normalizeClass as _normalizeClass, normalizeStyle as _normalizeStyle, resolveDynamicComponent as _resolveDynamicComponent, withCtx as _withCtx, createBlock as _createBlock, toDisplayString as _toDisplayString } from "vue"

const _hoisted_1 = { class: "crm:w-full crm:overflow-auto" }
const _hoisted_2 = {
  key: 0,
  class: "crm:p-4"
}
const _hoisted_3 = ["innerHTML"]
const _hoisted_4 = ["onClick"]
const _hoisted_5 = ["innerHTML"]
const _hoisted_6 = ["innerHTML"]
const _hoisted_7 = { class: "crm:text-center crm:italic" }

import {
  componentProps,
  useComponent
} from "fallco-frontend-framework/src/vue/composable/useComponent";
import {computed, useSlots, ref, provide, onMounted} from "vue";
import {httpPost} from "fallco-frontend-framework/src/js/classes/HttpManager/http_caller";
import {useRoute, useRouter} from "vue-router";
import {orderBy} from "lodash";


export default {
  __name: 'BaseTableBackend',
  props: {
  ...componentProps,
  /**
   * Dati della tabella
   */
  dati: {
    type: [Array, Object, null],
    required: true,
    default() {
      return [];
    },
  },
  /**
   * Dati default della tabella
   */
  datiDefault: {
    type: [Array, null],
    required: true,
    default() {
      return [];
    },
  },
  /**
   * Colonne da Mostrare. La chiave dovrebbe corrispondere ad una chiave dell'oggetto dati.
   * Puoi anche inserire chiavi non presenti nell'oggetto dati perchè ogni colonna genera uno slot dove
   * poter inserire dati e formattazioni custom.
   * Proprietà disponibili:
   * - name -> nome della colonna
   * - size -> dimensione della colonna
   * - sortable -> true/false se bisogna sortare per questa colonna
   * - sortable_map -> nella casistica di dati composti indicare la chiave per quale dato si desidera sortare
   * - is_hide_mobile -> se true, nasconde la colonna nel mobile
   * - is_badge -> se true, nella versione mobile mostra il valore della colonna all'interno di un badge in evidenza (per ogni tabella è possibile avere solo un is_badge a true)
   * - is_primary -> se true, nella versione mobile applica uno stile aggiuntivo
   * @example { 'descrizione_file': {name: 'Descrizione documento', size: '20%'},'last_mod_timestamp': {name: 'Ultima modifica', size: '10%', sortable: true}}
   */
  columns: {
    type: Object,
    default() {
      return {};
    },
  },
  /**
   * Messaggio da mostrare in caso di nessuna corrispondenza trovata
   */
  emptyMessage: {
    type: String,
    default: "Non è disponibile alcun contenuto",
  },
  /**
   * id della tabella
   */
  id: {
    type: String,
    required: true,
    default: "exampleTable",
  },
  /**
   * Se true le righe della tabella diventano cliccabili.
   * Al click viene generato un emit "onClickRow" dove poter aggiungere funzionalità custom
   */
  isRowClickable: {
    type: Boolean,
    default: false,
  },
  /**
   * Se true si occupa di aprire il panel dei filtri
   */
  isOpen: {
    type: Boolean,
    default: false,
  },
  /**
   * Numero di elementi nella tabella
   */
  totalItems: {
    type: Number,
    default: null,
  },

  /**
   * Parametri per le chiamate Ajax per l'esecuzione dei filtri
   * @example {controller: 'Pratiche', action:'getPratiche', path: 'pratiche'}
   */
  httpParams: {
    type: [Object],
    default() {
      return {
        controller: "",
        action: "",
        params: "",
        path: "",
      };
    },
    required: true,
  },
},
  emits: [
  /**
   * Emit dell'evento di click su una riga della tabella
   */
  "onClickRow",
  /**
   * emitta restore dei dati della tabella
   */
  "updateDatiTabella",
  /**
   * Emit dell'evento di aggiornamento dei dati della tabella
   */
  "update:dati",
  /**
   * Emit dell'evento di aggiornamento dei dati di default della tabella
   */
  "update:datiDefault",
  /**
   * Emitta l'aggiornamento del valore dei filtri applicati
   */
  "update:filters",
  /**
   * Emitta il numero totale di elementi restituiti dal backend
   */
  "update_totalItems",
],
  setup(__props, { emit: __emit }) {

/**
 * @doc Gestisce e renderizza una tabella
 * Versione Desktop: visualizzazione tabella
 * Versione Mobile: visualizzazione a card
 * Questa tabella gestisce filtri/paginazione/sorting attraverso chiamate al backend
 * Però passando alla props httpParams il valore isFrontendAction = true è possibile trasformare paginazione/sorting gestiti solo lato front
 * i filtri rimangono comunque gestiti attraverso chiamate al backend
 */
const props = __props;

const emits = __emit;

const componentUtilities = useComponent();

const isMobile = computed(() => {
  return componentUtilities.is_mobile_view.value;
});

const totalDataItems = ref(0)

const totalItemsPerPage = ref(0)

/**
 * Se true indica che la tabella possiede dei filtri
 * Questo valore viene autosettato in base se abbiamo inserito il componente base-filter-backend nello slot #content della tabella
 * attraverso il provide messo a disposizione sotto.
 * @type {Ref<UnwrapRef<boolean>>}
 */
const haveFilters = ref(false);
/**
 * @provides {updateHaveFilters} mette a disposizione dei componenti figli la possibilità di indicare se la tabella possiede dei filtri
 */
provide('updateHaveFilters', (status) => haveFilters.value = status)

const router = useRouter();
const route = useRoute();
const slots = useSlots();

const ajaxParams = ref({
  pagination: {
    currentPage: 1
  },
  filters: null,
  sorting: {}
})

const isEseguiPaginazione = ref(false)

/**
 * Aggiorna i dati che verranno usati per la chiamata Ajax che riaggiorna i dati della tabella
 * @param params
 */
function updateAjaxParams(params) {
  Object.keys(params).forEach((paramKey) => {
    ajaxParams.value[paramKey] = params[paramKey];
  })
}

/**
 * Esegue la chiamata per la cancellazione di un elemento in tabella
 * @return {Promise<void>}
 */
async function deleteElement(){
  ajaxParams.value.pagination.currentPage = 1;
  await eseguiAjax();
  updateIsEseguiPaginazione(1);
}

/**
 * Esegue la chiamata ajax per l'aggiornamento dei dati della tabella
 * Conclusa la chiamata esegue gli emit per ri-allineare i dati
 * @return {Promise<void>}
 */
async function eseguiAjax() {
  let params = {
    page: ajaxParams.value.pagination.currentPage,
    ...ajaxParams.value.filters
  }
  if(ajaxParams.value.sorting && Object.keys(ajaxParams.value.sorting).length > 0) {
    params['order'] = ajaxParams.value.sorting.colonnaOrdinata;
    params['verso'] = ajaxParams.value.sorting.colonnaOrdinataVerso;
  }

  if(props.httpParams['params'] !== undefined && props.httpParams['params']) {
    params = {...params, ...props.httpParams['params']}
  }

  let datiFiltrati = await httpPost(
      props.httpParams.controller,
      props.httpParams.action,
      params,
      {
        messages: 'error',
        headers: { "Content-Type": "multipart/form-data" },
      }
  );

  emits('update:dati', datiFiltrati.rows)
  emits('update:datiDefault', datiFiltrati.rows)
  emits("update_totalItems", datiFiltrati.calc_found_rows);
  totalDataItems.value = datiFiltrati.calc_found_rows;
  totalItemsPerPage.value = datiFiltrati.records_per_page ? datiFiltrati.records_per_page : 10;
}

/**
 *
 * @param status
 */
function updateIsEseguiPaginazione(status) {
  isEseguiPaginazione.value = status;
}

/**
 * Emitta l'aggiornamento dei dati della tabella
 * @param dati
 */
function updateTabella(dati) {
  emits('update:dati', dati)
}

provide('updateAjaxParams', updateAjaxParams)
provide('eseguiAjax', eseguiAjax)
provide('httpParams', props.httpParams)
provide('isEseguiPaginazione', computed(() => isEseguiPaginazione.value))
provide('updateIsEseguiPaginazione', updateIsEseguiPaginazione)
provide('totalDataItems', computed(() => totalDataItems.value))
provide('updateItemsPerPage',computed(()=> totalItemsPerPage.value))
provide('datiTabella', computed(() => props.dati))
provide('datiTabellaDefault', computed(() => props.datiDefault))
provide('updateTabella', updateTabella)
const sortingActived = ref({})
const columnActionsInTab = ref(false)

/**
 * Applica il sorting alla tabella e riporta la paginazione alla prima pagina
 * @param column
 * @returns {Promise<void>}
 */
async function applicaSorting(column) {
  ajaxParams.value.sorting['colonnaOrdinata'] = column;
  ajaxParams.value.sorting['colonnaOrdinataVerso'] =
      column === ajaxParams.value.sorting['colonnaOrdinata'] &&
      ajaxParams.value.sorting['colonnaOrdinataVerso'] === "asc"
          ? "desc"
          : "asc";
  await sort(ajaxParams.value.sorting['colonnaOrdinata']);
  if(props.httpParams['isFrontendAction'] === undefined || !props.httpParams['isFrontendAction']) {
    isEseguiPaginazione.value = true;
    ajaxParams.value.pagination.currentPage = 1;
    await eseguiAjax();
  }

}

/**
 * Gestisce l'azione di sorting
 * @param column
 */
function sort(column) {
  let directionToSort = "asc";
  if (column) {
    if (sortingActived.value && Object.keys(sortingActived.value).length > 0) {
      if (column in sortingActived.value) {
        directionToSort =
            sortingActived.value[column] === "asc" ? "desc" : "asc";
      } else {
        sortingActived.value = {};
      }
    }

    sortingActived.value[column] = directionToSort;
  }

  if(props.httpParams['isFrontendAction'] !== undefined && props.httpParams.isFrontendAction) {
    emits('update:dati', orderBy(props.datiDefault, [column], [directionToSort]));
  }
}

function emitClick(event, item) {
  if (
      props.isRowClickable &&
      event.target.tagName !== "INPUT" &&
      event.target.tagName !== "svg" &&
      !event.target.classList.contains("no-click-table-trigger")
  ) {
    emits("onClickRow", item);
  }
}

function getSlotName(name) {
  return name;
}

function isShowMobileLabel(item, index, datiKey, column) {
  let isMobileVersion =
      isMobile.value && !column.is_badge && !column.is_primary;
  let generalCondition = item[index] || slots[getSlotName(index, datiKey)];
  return isMobileVersion && generalCondition;
}

function checkIfActionsColExist() {
  let columns = Object.keys(props.columns);
  if (columns.includes('actions')) {
    columnActionsInTab.value = true;
  }
}

onMounted(async () => {
  if(!haveFilters.value) {
    await updateAjaxParams({pagination: {currentPage: 1}})
    await eseguiAjax();
    await updateIsEseguiPaginazione(true)
  }
  checkIfActionsColExist()
})
function getValues(obj, formatter) {
  return dispatch(formatter, obj);
}

const dispatch = (fn, args) => fn(args);

return (_ctx, _cache) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createCommentVNode(" @slot usato per inserire il componente base-table-backend-filter "),
    _renderSlot(_ctx.$slots, "content"),
    (__props.dati && __props.dati.length > 0)
      ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
          _createElementVNode("div", _hoisted_1, [
            (_openBlock(), _createBlock(_resolveDynamicComponent(isMobile.value ? 'div' : 'table'), {
              id: __props.id,
              class: _normalizeClass(["crm:w-full", { 'crm:border-collapse': !isMobile.value }])
            }, {
              default: _withCtx(() => [
                _createCommentVNode(" intestazione "),
                (!isMobile.value)
                  ? (_openBlock(), _createElementBlock("thead", _hoisted_2, [
                      _createElementVNode("tr", null, [
                        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(__props.columns, (column, columnKey) => {
                          return (_openBlock(), _createElementBlock("th", {
                            key: `th-${columnKey}`,
                            style: _normalizeStyle({
                background: '#859aab',
                width: column.size,
                textAlign: column.align
              }),
                            class: "crm:border! crm:border-solid! crm:border-white! crm:p-3! crm:tracking-wider! crm:text-white!"
                          }, [
                            _createElementVNode("span", {
                              innerHTML: column.name
                            }, null, 8 /* PROPS */, _hoisted_3),
                            (column.sortable)
                              ? (_openBlock(), _createElementBlock("span", {
                                  key: 0,
                                  class: _normalizeClass(["crm:ml-2 crm:cursor-pointer", { 'c-pointer': __props.isRowClickable }]),
                                  onClick: $event => (
                  applicaSorting(
                    column['sortable_map'] !== undefined
                      ? column['sortable_map']
                      : columnKey
                  )
                )
                                }, [
                                  _createElementVNode("i", {
                                    class: _normalizeClass(["fas", {
                    'fa-sort':
                      !sortingActived.value[
                        column['sortable_map'] !== undefined
                          ? column['sortable_map']
                          : columnKey
                      ],
                    'fa-sort-up':
                      sortingActived.value[
                        column['sortable_map'] !== undefined
                          ? column['sortable_map']
                          : columnKey
                      ] === 'asc',
                    'fa-sort-down':
                      sortingActived.value[
                        column['sortable_map'] !== undefined
                          ? column['sortable_map']
                          : columnKey
                      ] === 'desc',
                  }])
                                  }, null, 2 /* CLASS */)
                                ], 10 /* CLASS, PROPS */, _hoisted_4))
                              : _createCommentVNode("v-if", true)
                          ], 4 /* STYLE */))
                        }), 128 /* KEYED_FRAGMENT */))
                      ])
                    ]))
                  : _createCommentVNode("v-if", true),
                _createCommentVNode(" corpo "),
                (_openBlock(), _createBlock(_resolveDynamicComponent(isMobile.value ? 'div' : 'tbody'), null, {
                  default: _withCtx(() => [
                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(__props.dati, (item, datiKey) => {
                      return (_openBlock(), _createBlock(_resolveDynamicComponent(isMobile.value ? 'div' : 'tr'), {
                        key: `td-${datiKey}`,
                        class: _normalizeClass([{
              'crm:relative crm:mb-2 crm:inline-block crm:w-full crm:bg-white crm:py-6 crm:sm:py-6 crm:md:py-6 crm:border crm:border-solid crm:border-gray-200':
                isMobile.value && !columnActionsInTab.value,
              'crm:relative crm:mb-2 crm:inline-block crm:w-full crm:bg-white crm:pt-12 crm:pb-8 crm:sm:pt-12 crm:sm:pb-8 crm:md:pt-12 crm:md:pb-8 crm:border crm:border-solid crm:border-gray-200':
                isMobile.value && columnActionsInTab.value,
              pulse: item.lighting,
              'crm:cursor-pointer crm:hover:bg-[#E7F5FF]': __props.isRowClickable,
            }, "crm:bg-white"]),
                        onClick: $event => (emitClick($event, item))
                      }, {
                        default: _withCtx(() => [
                          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(__props.columns, (column, index) => {
                            return (_openBlock(), _createElementBlock(_Fragment, null, [
                              (
                  ((item[index] || _ctx.$slots[getSlotName(index, datiKey)]) &&
                    isMobile.value &&
                    !column.is_hide_mobile) ||
                  !isMobile.value
                )
                                ? (_openBlock(), _createBlock(_resolveDynamicComponent(isMobile.value ? 'span' : 'td'), {
                                    key: 0,
                                    class: _normalizeClass(["crm:flex-wrap", [
                    {
                      'crm:flex crm:flex-col crm:sm:flex-col crm:md:flex-row crm:lg:flex-row crm:px-3 crm:pt-1 crm:pb-1 crm:sm:py-1 crm:md:py-1': isMobile.value && getSlotName(index, datiKey) !== 'actions',
                      'crm:absolute crm:top-0 crm:border-b crm:border-solid crm:border-gray-200 crm:flex crm:items-center crm:justify-end crm:mt-1 crm:w-full crm:h-[40px]': isMobile.value && getSlotName(index, datiKey) === 'actions',
                      'crm:border! crm:border-solid! crm:border-gray-200! crm:p-3':
                        !isMobile.value,
                    },
                    !isMobile.value && column.align ? `crm:text-${column.align}` : ''
                    ]])
                                  }, {
                                    default: _withCtx(() => [
                                      (isShowMobileLabel(item, index, datiKey, column))
                                        ? (_openBlock(), _createElementBlock("strong", {
                                            key: 0,
                                            class: _normalizeClass(isMobile.value && getSlotName(index, datiKey) === 'actions' ? '':'crm:mr-2'),
                                            innerHTML: `${column.name !== '' ? column.name + ':' : ''}`
                                          }, null, 10 /* CLASS, PROPS */, _hoisted_5))
                                        : _createCommentVNode("v-if", true),
                                      _createElementVNode("span", {
                                        class: _normalizeClass([
                    column.is_badge && isMobile.value
                      ? 'crm:success-100 crm:absolute crm:right-2 crm:top-0 crm:rounded-xs crm:p-2'
                      : '',
                  ])
                                      }, [
                                        _createCommentVNode(" @slot possibile inserire un contenuto specifico per ogni riga di una colonna specifica "),
                                        (_ctx.$slots[getSlotName(index, datiKey)])
                                          ? _renderSlot(_ctx.$slots, getSlotName(index, datiKey), {
                                              key: 0,
                                              deleteElement: deleteElement,
                                              elemento: item
                                            })
                                          : (_openBlock(), _createElementBlock("span", {
                                              key: 1,
                                              innerHTML: column['formatter'] !== undefined ? getValues(item, column['formatter']) : item[index] && item[index] !== '0' ? item[index] : '-'
                                            }, null, 8 /* PROPS */, _hoisted_6))
                                      ], 2 /* CLASS */)
                                    ]),
                                    _: 2 /* DYNAMIC */
                                  }, 1032 /* PROPS, DYNAMIC_SLOTS */, ["class"]))
                                : _createCommentVNode("v-if", true)
                            ], 64 /* STABLE_FRAGMENT */))
                          }), 256 /* UNKEYED_FRAGMENT */))
                        ]),
                        _: 2 /* DYNAMIC */
                      }, 1032 /* PROPS, DYNAMIC_SLOTS */, ["class", "onClick"]))
                    }), 128 /* KEYED_FRAGMENT */))
                  ]),
                  _: 3 /* FORWARDED */
                }))
              ]),
              _: 3 /* FORWARDED */
            }, 8 /* PROPS */, ["id", "class"]))
          ]),
          _createCommentVNode(" @slot usato per inserire il componente base-table-backend-pagination "),
          _renderSlot(_ctx.$slots, "pagination")
        ], 64 /* STABLE_FRAGMENT */))
      : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
          _createCommentVNode(" Messaggio Dati non presenti "),
          _createElementVNode("p", _hoisted_7, _toDisplayString(__props.emptyMessage), 1 /* TEXT */)
        ], 2112 /* STABLE_FRAGMENT, DEV_ROOT_FRAGMENT */))
  ], 64 /* STABLE_FRAGMENT */))
}
}

}